import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, Check, PackageMinus, Pencil, X } from 'lucide-react'
import { toast } from 'sonner'

import { agriOutReasonLabel } from './index'
import { PageBackButton } from '@/components/common/PageBackButton'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { WarehouseNoteStatusBadge } from '@/components/warehouse/note-status-badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import {
    useAgriOutNote,
    useApproveAgriOutNote,
    useCancelAgriOutNote,
    useRejectAgriOutNote,
    useRollbackAgriOutNote,
} from '@/hooks/use-agri-material-lot-query'
import {
    NoteRollbackButton,
    NoteRollbackLinks,
} from '@/components/warehouse/note-rollback'
import { formatDateTimeVN, formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/agricultural-materials/stock-out-notes/$id',
)({
    component: AgriOutNoteDetailPage,
})

function AgriOutNoteDetailPage() {
    const { id } = Route.useParams()
    const { user, hasPermission } = useAuth()
    const { data: res, isLoading } = useAgriOutNote(id)
    const approve = useApproveAgriOutNote()
    const reject = useRejectAgriOutNote()
    const cancel = useCancelAgriOutNote()
    const rollback = useRollbackAgriOutNote()
    const note = res?.data
    const [rejectOpen, setRejectOpen] = useState(false)
    const [rejectReason, setRejectReason] = useState('')
    const approveWorkflow = useWorkflowTracking('agricultural_stock_out', {
        autoStart: false,
        attemptKey: id,
        operation: 'approve',
    })
    const rejectWorkflow = useWorkflowTracking('agricultural_stock_out', {
        active: rejectOpen,
        attemptKey: id,
        operation: 'reject',
    })
    const cancelWorkflow = useWorkflowTracking('agricultural_stock_out', {
        autoStart: false,
        attemptKey: id,
        operation: 'cancel',
    })

    if (isLoading) return <div className="p-6">{m.sl_detail_loading()}</div>
    if (!note) return <div className="p-6">{m.sl_detail_note_not_found()}</div>

    const isPending = note.status === 'pending'
    const isCreator = user?.id && note.created_by_id === user.id
    const canApprove = hasPermission('agri_material_lots.approve_out')
    const canWrite = hasPermission('agri_material_lots.write')

    const handleApprove = async () => {
        if (!confirm(m.sl_detail_confirm_approve_out())) return
        try {
            await approveWorkflow.run(() => approve.mutateAsync(note.id))
        } catch (err) {
            toast.error(
                err instanceof Error
                    ? err.message
                    : m.sl_detail_error_approve(),
            )
        }
    }

    const handleReject = async () => {
        try {
            await rejectWorkflow.run(() =>
                reject.mutateAsync({ id: note.id, reason: rejectReason }),
            )
            setRejectOpen(false)
            setRejectReason('')
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_detail_error_reject(),
            )
        }
    }

    const handleCancel = async () => {
        if (!confirm(m.sl_detail_confirm_cancel_out())) return
        try {
            await cancelWorkflow.run(() => cancel.mutateAsync(note.id))
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_detail_error_cancel(),
            )
        }
    }

    return (
        <div className="space-y-4 page-pad">
            <Card>
                <CardHeader>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <PageBackButton asChild>
                                <Link to="/agricultural-materials/stock-out-notes">
                                    <ArrowLeft className="size-4" />
                                </Link>
                            </PageBackButton>
                            <PackageMinus className="size-5" />
                            <CardTitle className="font-mono text-base md:text-2xl">
                                {note.code}
                            </CardTitle>
                            <WarehouseNoteStatusBadge status={note.status} />
                        </div>
                        {isPending && (
                            <div className="flex flex-wrap gap-2">
                                {isCreator && canWrite && (
                                    <Button
                                        asChild
                                        variant="outline"
                                        className="h-11 md:h-9"
                                    >
                                        <Link
                                            to="/agricultural-materials/stock-out-notes/$id/edit"
                                            params={{ id: note.id }}
                                        >
                                            <Pencil className="mr-1 size-4" />
                                            {m.pl_pnk_edit_btn()}
                                        </Link>
                                    </Button>
                                )}
                                {canApprove && (
                                    <>
                                        <Button
                                            className="h-11 md:h-9"
                                            onClick={handleApprove}
                                            disabled={approve.isPending}
                                        >
                                            <Check className="mr-1 size-4" />{' '}
                                            {m.sl_detail_action_approve()}
                                        </Button>
                                        <Button
                                            className="h-11 md:h-9"
                                            variant="destructive"
                                            onClick={() => setRejectOpen(true)}
                                        >
                                            <X className="mr-1 size-4" />{' '}
                                            {m.sl_detail_action_reject()}
                                        </Button>
                                    </>
                                )}
                                {isCreator && canWrite && (
                                    <Button
                                        className="h-11 md:h-9"
                                        variant="outline"
                                        onClick={handleCancel}
                                        disabled={cancel.isPending}
                                    >
                                        {m.sl_detail_action_cancel()}
                                    </Button>
                                )}
                            </div>
                        )}
                        {/* PXK gắn nhật ký chăm sóc vẫn rollback được (không phải đơn hàng). */}
                        <NoteRollbackButton
                            noteId={note.id}
                            status={note.status}
                            canRollback={canApprove && !note.care_log_id}
                            rolledBackBy={note.rolled_back_by}
                            rollbackOf={note.rollback_of}
                            confirmMessage={m.note_rollback_confirm_out()}
                            pending={rollback.isPending}
                            onRollback={() => rollback.mutateAsync(note.id)}
                        />
                    </div>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                        <Field
                            label={m.sl_detail_label_reason()}
                            value={agriOutReasonLabel(note.reason)}
                        />
                    </div>
                    <Field
                        label={m.sl_detail_label_created_at()}
                        value={formatDateTimeVN(note.created_at)}
                    />
                    <Field
                        label={m.sl_detail_label_created_by()}
                        value={note.created_by_name ?? '—'}
                    />
                    {note.approved_at && (
                        <>
                            <Field
                                label={m.sl_detail_label_approved_at()}
                                value={formatDateTimeVN(note.approved_at)}
                            />
                            <Field
                                label={
                                    note.status === 'approved'
                                        ? m.sl_detail_label_approver()
                                        : m.sl_detail_label_rejecter()
                                }
                                value={note.approved_by_name ?? '—'}
                            />
                        </>
                    )}
                    {note.cancelled_at && (
                        <div className="col-span-2">
                            <Field
                                label={m.sl_detail_label_cancelled_at()}
                                value={formatDateTimeVN(note.cancelled_at)}
                            />
                        </div>
                    )}
                    {note.notes && (
                        <div className="col-span-2">
                            <Field
                                label={m.sl_detail_label_notes()}
                                value={note.notes}
                            />
                        </div>
                    )}
                    {note.rejected_reason && (
                        <div className="col-span-2">
                            <Field
                                label={m.sl_detail_label_rejected_reason()}
                                value={note.rejected_reason}
                            />
                        </div>
                    )}
                    <NoteRollbackLinks
                        rolledBackBy={note.rolled_back_by}
                        rollbackOf={note.rollback_of}
                        renderLink={(ref) => (
                            <Link
                                to="/agricultural-materials/stock-in-notes/$id"
                                params={{ id: ref.id }}
                                className="text-primary hover:underline"
                            >
                                {ref.code}
                            </Link>
                        )}
                    />
                </CardContent>
            </Card>

            <div className="overflow-x-auto rounded-md border bg-card">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.sl_lots_col_code()}</TableHead>
                            <TableHead>{m.aml_form_material()}</TableHead>
                            <TableHead>{m.aml_form_grower()}</TableHead>
                            <TableHead className="text-right">
                                {m.sl_detail_col_out()}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {(note.items ?? []).map((it) => (
                            <TableRow key={it.id}>
                                <TableCell className="font-mono text-sm">
                                    <Link
                                        to="/agricultural-materials/lots/$id"
                                        params={{ id: it.agri_material_lot_id }}
                                        className="text-primary hover:underline"
                                    >
                                        {it.lot_code ?? '—'}
                                    </Link>
                                </TableCell>
                                <TableCell>{it.material_name ?? '—'}</TableCell>
                                <TableCell>{it.grower_name || '—'}</TableCell>
                                <TableCell className="text-right tabular-nums">
                                    {formatQty(it.quantity)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {m.sl_detail_dialog_reject_title_out()}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label>{m.sl_detail_dialog_reject_label()}</Label>
                        <Input
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder={m.sl_detail_dialog_reject_placeholder()}
                        />
                    </div>
                    <DialogFooter className="flex-row gap-2 sm:justify-end">
                        <Button
                            variant="outline"
                            className="h-11 flex-1 sm:h-10 sm:flex-none"
                            onClick={() => setRejectOpen(false)}
                        >
                            {m.sl_detail_dialog_cancel_btn()}
                        </Button>
                        <Button
                            variant="destructive"
                            className="h-11 flex-1 sm:h-10 sm:flex-none"
                            onClick={handleReject}
                            disabled={reject.isPending}
                        >
                            {m.sl_detail_dialog_confirm_reject_btn()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}

function Field({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-base font-medium md:text-sm">{value}</p>
        </div>
    )
}
